import React, { useEffect } from "react";
import { Box, Collapse, IconButton } from "@mui/material";
import {
  Add,
  DeleteOutlineRounded,
  KeyboardArrowDownRounded,
  KeyboardArrowUpRounded,
} from "@mui/icons-material";
import ScoreDefinitionCard from "./ScoreDefinitionCard";
import ClickAndEditText from "../ClickAndEditText";
import { ReactComponent as TrashIcon } from "../../assets/icons/trash-blue.svg";
import RubricPointsChip from "./RubricPointsChip";

const colors = ["#079455", "#DC6803", "#C11574", "#1A73E8", "#E8A200"];

let timeout = null;

const CriterionCard = ({
  startFrom = 0,
  criterion = {},
  sortDesc = false,
  score_titles = {},
  score_colors = colors,
  editing,
  defaultExpanded = false,
  deleteCriterion = () => {},
  onUpdateCriterion = () => {},
  onUpdateScoreTitles = () => {},
}) => {
  const [open, setOpen] = React.useState(editing || false);
  const [title, setTitle] = React.useState(criterion?.title);
  const [description, setDescription] = React.useState(criterion?.description);
  const { score_definitions = [] } = criterion;

  const scores = sortDesc
    ? score_definitions?.slice()?.reverse()
    : score_definitions;
  useEffect(() => {
    if (defaultExpanded) setOpen(true);
    if (!editing) return;
    setOpen(editing);
  }, [editing]);

  const handleDeleteCriterion = () => {
    deleteCriterion(criterion?.criterion_id);
  };

  const addScore = () => {
    const newScoreDefinition = {
      score_id: `score_${criterion.score_definitions.length + 1}`,
      level: criterion.score_definitions.length + startFrom,
      definition: "",
    };
    onUpdateCriterion(criterion?.criterion_id, {
      ...criterion,
      score_definitions: [...criterion.score_definitions, newScoreDefinition],
    });
  };

  const handleUpdateScore = (score_id, updates = {}) => {
    const newScoreDefinitions = criterion.score_definitions.map((score) => {
      if (score.score_id === score_id) {
        return {
          ...score,
          ...updates,
        };
      }
      return score;
    });
    updateCriterion({ score_definitions: newScoreDefinitions });
  };
  const deleteScore = () => {
    const newScoreDefinitions = criterion.score_definitions.slice(0, -1);
    onUpdateCriterion(criterion?.criterion_id, {
      ...criterion,
      score_definitions: newScoreDefinitions,
    });
  };

  const updateCriterion = (updates = {}) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => {
      onUpdateCriterion(criterion?.criterion_id, {
        ...criterion,
        ...updates,
      });
    }, 1000);
  };

  const maxPoints = scores
    ?.map((s) => s.level)
    .reduce((a, b) => Math.max(a, b), 0);

  useEffect(() => {
    setTitle(criterion?.title);
    setDescription(criterion?.description);
  }, [criterion]);
  return (
    <Box
      onClick={() => {
        if (editing) return;
        setOpen(!open);
      }}
      sx={{
        p: "16px",
        borderRadius: "16px",
        border: editing ? "1px solid #D0D5DD" : "none",
        bgcolor: editing ? "white" : "#F2F4F7",
        cursor: "pointer",
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
        }}
      >
        <Box
          sx={{
            width: "100%",
          }}
        >
          <Box>
            <Box
              sx={{
                display: "flex",
                gap: "8px",
                alignItems: "center",
              }}
            >
              <ClickAndEditText
                value={title}
                onChange={(value) => {
                  setTitle(value);
                  updateCriterion({ title: value });
                }}
                editable={editing}
                placeholder="Criteria Title"
                sx={{
                  fontSize: 20,
                  fontWeight: 500,
                  // width: "fit-content",
                }}
              />
              <RubricPointsChip
                title={`${maxPoints} points`}
                colors={{
                  borderColor: "#D9D6FE",
                  bgcolor: "#F4F3FF",
                  color: "#5925DC",
                }}
              />
            </Box>
            <ClickAndEditText
              value={description}
              onChange={(value) => {
                setDescription(value);
                updateCriterion({ description: value });
              }}
              editable={editing}
              placeholder="Criteria Description"
              sx={{
                fontSize: 16,
                fontWeight: 400,
                width: "100%",
              }}
              InputBaseProps={{
                multiline: true,
              }}
            />
          </Box>
        </Box>
        <Box>
          {editing ? (
            <IconButton onClick={handleDeleteCriterion}>
              <TrashIcon />
            </IconButton>
          ) : (
            <IconButton>
              {open ? <KeyboardArrowUpRounded /> : <KeyboardArrowDownRounded />}
            </IconButton>
          )}
        </Box>
      </Box>
      <Collapse in={open}>
        <Box
          sx={{
            display: "flex",
            gap: "4px",
            mt: "12px",
            width: "100%",
            overflowX: "auto",
            pb: 2,
            // maxWidth: "700px",
          }}
        >
          {editing && sortDesc && (
            <ScoreDefinitionActions
              criterion={criterion}
              onAddScore={addScore}
              onDeleteCriterion={deleteScore}
            />
          )}
          {scores &&
            scores.map((score, index) => (
              <ScoreDefinitionCard
                key={index}
                index={sortDesc ? score_definitions?.length - 1 - index : index}
                score={score}
                title={
                  score_titles[
                    sortDesc ? score_definitions?.length - 1 - index : index
                  ]
                }
                color={
                  score_colors[
                    sortDesc ? index : score_definitions?.length - 1 - index
                  ]
                }
                editing={editing}
                onUpdateScore={handleUpdateScore}
                onUpdateTitle={onUpdateScoreTitles}
              />
            ))}
          {editing && !sortDesc && (
            <ScoreDefinitionActions
              criterion={criterion}
              onAddScore={addScore}
              onDeleteCriterion={deleteScore}
            />
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default React.memo(CriterionCard);

const ScoreDefinitionActions = ({
  criterion,
  onAddScore,
  onDeleteCriterion,
}) => {
  const buttons = [
    {
      icon: <Add color="secondary" />,
      onClick: onAddScore,
    },
    {
      icon: <TrashIcon />,
      onClick: onDeleteCriterion,
    },
  ];
  return (
    <Box
      sx={{
        display: "flex",
        gap: "4px",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {buttons.map(({ onClick, icon }, index) => (
        <Box
          key={index}
          sx={{
            width: "44px",
            height: "44px",
            borderRadius: "16px",
            border: "1px solid #D0D5DD",
            boxShadow: "0px 1px 16px 0px rgba(16, 24, 40, 0.10)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            "&:hover": {
              bgcolor: "#F9FAFB",
            },
          }}
          onClick={onClick}
        >
          {icon}
        </Box>
      ))}
    </Box>
  );
};
